import { CreateInDialogButtonProps } from '@react-admin/ra-form-layout';
import { useCallback, useMemo } from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import { FieldValues, UseFormReturn } from 'react-hook-form';

import { CreateInDialogButton, StepperForm, StepperFormStep } from '@sh4res/bo-common/components';
import { RecurringInvestmentGeneralInfoStep } from '@sh4res/bo-common/components/B2bRecurringInvestments/RecurringInvestmentGeneralInfoStep';
import { useRecurringInvestmentCreate } from '@sh4res/bo-common/components/B2bRecurringInvestments/useRecurringInvestmentCreate';
import { AmountsStep } from '@sh4res/bo-common/components/B2bVoluntaryPayment/steps/AmountsStep';
import { ConfirmationStep } from '@sh4res/bo-common/components/B2bVoluntaryPayment/steps/ConfirmationStep';
import { PaymentStep } from '@sh4res/bo-common/components/B2bVoluntaryPayment/steps/PaymentStep';
import { validateForm } from '@sh4res/bo-common/components/StepperFormButtons';
import { useResourceBasePath } from '@sh4res/bo-common/hooks';

const RecurringInvestmentForm = ({ steps }: { steps: StepperFormStep[] }) => {
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const resourceBasePath = useResourceBasePath();
  const saveRecurringInvestment = useRecurringInvestmentCreate();

  const handleSave = useCallback(
    async (form: UseFormReturn<FieldValues>) => {
      if (!(await validateForm(form, notify, dataProvider, resourceBasePath))) {
        return;
      }
      await form.handleSubmit(saveRecurringInvestment)();
    },
    [notify, dataProvider, resourceBasePath, saveRecurringInvestment],
  );

  return (
    <StepperForm
      steps={steps}
      defaultValues={{ type: 'recurring_voluntary_payment', payer: 'employee' }}
      onSave={handleSave}
      permissions={{
        resource: 'b2b/recurring_investments',
        action: 'create',
      }}
    />
  );
};

export function B2bRecurringInvestmentCreate(props: CreateInDialogButtonProps['ButtonProps']) {
  const resourceBasePath = useResourceBasePath();

  const steps: StepperFormStep[] = useMemo(
    () => [
      {
        label: 'resources.bo_common.b2b.voluntary_payment_create.steps.1',
        content: <RecurringInvestmentGeneralInfoStep />,
      },
      {
        label: 'resources.bo_common.b2b.voluntary_payment_create.steps.2',
        content: <AmountsStep />,
      },
      {
        label: 'resources.bo_common.b2b.voluntary_payment_create.steps.3',
        content: <PaymentStep sepaOnly />,
        nextDisabled: (values) => values.has_valid_mandate === false,
      },
      {
        label: 'resources.bo_common.b2b.voluntary_payment_create.steps.4',
        content: <ConfirmationStep recurring />,
      },
    ],
    [],
  );

  return (
    <CreateInDialogButton
      resource={`${resourceBasePath}recurring_investments`}
      testId="BoExternalB2bRecurringInvestmentCreate_CreateButton"
      fullWidth
      maxWidth="xl"
      title="resources.bo_common.b2b.recurring_investments.create.title"
      ButtonProps={{
        label: 'resources.bo_common.b2b.recurring_investments.create.button',
        ...props,
      }}>
      <RecurringInvestmentForm steps={steps} />
    </CreateInDialogButton>
  );
}
